import "./ClientStyles.css";

import React, { useEffect, useReducer, useState } from "react";
import { ThemeProvider, createUseStyles } from "react-jss";
import { TimeoutError, timeout } from "promise-timeout";
import {
  allPass,
  any,
  complement,
  equals,
  filter,
  isNil,
  juxt,
  last,
  length,
  mergeDeepRight,
  partial,
  path,
  pipe,
  propEq,
} from "ramda";
import analyticsLib, {
  eventTypes,
  extractEventsFromAction,
} from "../lib/analytics";
import { featureNames, isFeatureEnabled } from "../lib/EnabledFeatures";
import { hideClass, isMobile } from "../lib/utils";
import { useDoOnce, usePrevious } from "../customHooks";

import AnalyticsInjector from "./AnalyticsInjector";
import ClosedWidget from "./Widgets/ClosedWidget/ClosedWidget";
import Header from "./WidgetInterface/Header/Header";
import Panels from "./Panels";
import USER_ACTIONS from "./UserActions";
import connect from "client-socket";
import defaultStyles from "../styles/defaultStyles";
import { detect } from "detect-browser";
import { deviceDetect } from "react-device-detect";
import doHttpRequest from "../lib/HTTPRequest";
import { executeStoredCommands } from "../lib/BrowserCommands";
import getTheme from "../styles/Theme";
import { makeStylesFromThemeAndCustomStyles } from "../lib/StyleFunctions";
import preRecordedMessages from "../lib/audio/audio";
import { runBrowserCommands } from "../lib/BrowserCommands";
import { updateCSSVariables } from "../lib/utils";

const TRANSCRIBE_BUBBLE_PLACEHOLDER = "Listening...";
const POSITIVE_FEEDBACK_TEXT = "Yes, the assistant was easy to use";
const NEGATIVE_FEEDBACK_TEXT = "No, the assistant was not easy to use";

const RECORDING_STATES = {
  NOT_RECORDING: "NOT_RECORDING",
  RECORDING: "RECORDING",
  WAITING_FOR_MIC: "WAITING_FOR_MIC",
};

const isStateRecording = equals(RECORDING_STATES.RECORDING);

const useStateStable = (value) => useReducer((_, action) => action, value);

const onStartRecordingClick = ({
  setTranscribedText,
  soundController,
  sttController,
  socketHandlers,
  setRecordingState,
  addToHistory,
}) => async () => {
  setTranscribedText(TRANSCRIBE_BUBBLE_PLACEHOLDER);
  try {
    setRecordingState(RECORDING_STATES.WAITING_FOR_MIC);
    await timeout(
      sttController.startRecording({
        sendAudioSamples: socketHandlers.sendAudioSamples,
        startRecording: (sampleRate, sampleSize) => {
          soundController.stop();
          socketHandlers.sendRecordingStreamStart(sampleRate, sampleSize);
        },
      }),
      6000
    );
    setRecordingState((prev) =>
      prev === RECORDING_STATES.WAITING_FOR_MIC
        ? RECORDING_STATES.RECORDING
        : RECORDING_STATES.NOT_RECORDING
    );
  } catch (e) {
    if (e instanceof TimeoutError || e instanceof DOMException) {
      const messageName = "allowMicFirstMessage";
      addToHistory({
        type: eventTypes.BOT_UTTERANCE,
        data: {
          text: preRecordedMessages[messageName].text,
          suggestions: [],
          hints: [],
          results: [],
          tags: [],
        },
      });
      soundController.playPreRecordedMessage(messageName);
    }
  }
};

const doActionSideEffects = (action, sendReadResult) => {
  const { browserCommands, httpRequest } = action;
  if (browserCommands && browserCommands.length) {
    runBrowserCommands(window, browserCommands, sendReadResult);
  }
  if (httpRequest) {
    doHttpRequest(httpRequest);
  }
};

const isConversationEmpty = complement(
  any(propEq("type", eventTypes.BOT_UTTERANCE))
);

const showPrompt = (conversationHistory, promptMessages, features) =>
  allPass([
    isConversationEmpty,
    () => promptMessages.length,
    complement(() => isMobile() && features.blockPromptOnMobile),
    complement(
      allPass([
        any(propEq("type", eventTypes.PROMPT_LOADED)),
        () => features.showPromptsOnlyOnce,
      ])
    ),
    complement(
      allPass([
        any(propEq("type", eventTypes.PROMPT_CLOSED)),
        () => features.hidePromptsAfterClosed,
      ])
    ),
  ])(conversationHistory);

const onFeatures = (setFeatures, features) => {
  features.customWidgetStyle =
    isMobile() && features.customWidgetMobileStyle
      ? features.customWidgetMobileStyle
      : features.customWidgetStyle;
  setFeatures(features);
  hideClass(features.classToHide);
};

const connectSocket = ({
  id,
  addToHistory,
  clearHistory,
  setFeatures,
  setPrompt,
  soundController,
  setIsThinking,
  setSocketHandlers,
  setTheme,
  addConnectionEvent,
  setHistoryFromServer,
}) =>
  setSocketHandlers(
    connect({
      serverUrl:
        window.HYRO_WIDGET_SERVER_URL || process.env.REACT_APP_SOCKET_SERVER,
      botId: id,
      location: window.location,
      connectionEvents: [
        {
          type: eventTypes.CONNECTION_EVENT,
          data: { location: window.location },
        },
        // TODO(Rom): remove `WIDGET_LOAD` after `CONNECTION_EVENT` is merged in nlu-engine/
        {
          type: eventTypes.WIDGET_LOAD,
          data: {
            location: window.location,
            referrer: document.referrer,
          },
        },
      ],
      deviceInfo: detect(),
      eventHandlers: {
        onConnected: ({
          theme,
          features,
          conversationHistory,
          promptMessages,
        }) => {
          clearHistory();
          conversationHistory
            .filter(({ type }) =>
              [eventTypes.BOT_UTTERANCE, eventTypes.USER_UTTERANCE].includes(
                type
              )
            )
            .forEach(addToHistory);
          setHistoryFromServer(conversationHistory);
          setTheme(getTheme(features.theme || theme));
          onFeatures(setFeatures, features);
          updateCSSVariables(features.theme || theme);
          if (showPrompt(conversationHistory, promptMessages, features)) {
            setTimeout(() => setPrompt(promptMessages[0]), 750);
          }
          addConnectionEvent(true);
        },
        onError: console.error,
        onAgentMessage: (action) => {
          addToHistory({ type: eventTypes.BOT_UTTERANCE, data: action });
        },
        onFeatures: partial(onFeatures, [setFeatures]),
        onAudioData: (audioData) => soundController.play(audioData),
        onNoAnswer: () => {
          const messageName = "noAnswer";
          addToHistory({
            type: eventTypes.BOT_UTTERANCE,
            data: {
              text: preRecordedMessages[messageName].text,
              suggestions: [],
              hints: [],
              results: [],
              tags: [],
            },
          });
          soundController.playPreRecordedMessage(messageName);
        },
        onDisconnect: () => addConnectionEvent(false),
        onThinking: ({ on }) => {
          setIsThinking(on);
        },
      },
    })
  );

const blockPages = (features, setIsHidden, history) => {
  if (process.env.REACT_APP_ENV === "production" && features) {
    if (
      features.showOnlyOnPageWithClass &&
      document.getElementsByClassName(features.showOnlyOnPageWithClass)
        .length === 0
    ) {
      setIsHidden(true);
      return;
    }

    let urlFilters;
    let shouldBlockMatch;
    if (features.forbiddenUrls) {
      urlFilters =
        (isMobile()
          ? features.forbiddenUrls.mobile
          : features.forbiddenUrls.desktop) || [];
      shouldBlockMatch = (match) => !!match;
    } else if (features.allowedUrls) {
      urlFilters =
        (isMobile()
          ? features.allowedUrls.mobile
          : features.allowedUrls.desktop) || [];
      shouldBlockMatch = (match) => !match;
    }
    if (urlFilters) {
      setIsHidden(
        urlFilters.some((urlFilter) =>
          shouldBlockMatch(window.location.href.match(new RegExp(urlFilter)))
        ) && !historyHasUserUtterance(history)
      );
    }
  }
};

const reduceActionHistory = (state, userAction) =>
  [USER_ACTIONS.MINIMIZE_BUTTON, USER_ACTIONS.ESC_CLICK].includes(userAction)
    ? false
    : [
        USER_ACTIONS.FAB_BUTTON,
        USER_ACTIONS.PROMPT_SUGGESTION_CLICK,
        USER_ACTIONS.EXTERNAL_SEARCH,
      ].includes(userAction)
    ? true
    : !state;

const addToHistory = (mutateHistory) => (event) =>
  mutateHistory((state) => [...state, event]);

const sendUtterance = (mutateHistory, socketHandlers, analytics) => (
  utterance
) => {
  addToHistory(mutateHistory)({
    type: eventTypes.USER_UTTERANCE,
    data: {
      utterance,
      type: "text",
    },
  });
  analytics.sendAnalyticsEvent({
    type: eventTypes.USER_UTTERANCE,
    data: {
      utterance,
      method: "textQuery",
    },
  });
  socketHandlers.sendTextUtterance(utterance);
};

const sendUtteranceSuggestion = (mutateHistory, socketHandlers) => (
  utterance
) => {
  addToHistory(mutateHistory)({
    type: eventTypes.USER_UTTERANCE,
    data: {
      utterance,
      type: "suggestion",
    },
  });
  socketHandlers.sendUtteranceSuggestion(utterance);
};

const showFeedback = (setPrompt, setDidGetFeedback) =>
  setTimeout(() => {
    setPrompt({
      text: "Was this assistant easy to use?",
      suggestions: [
        { displayText: "Yes", sayText: POSITIVE_FEEDBACK_TEXT },
        {
          displayText: "No",
          sayText: NEGATIVE_FEEDBACK_TEXT,
        },
      ],
    });
    setDidGetFeedback(true);
  }, 1000);

const getTextOnSuggestionClick = (
  text,
  addToHistory,
  sendUtteranceSuggestion,
  sendEvent,
  addUserAction
) => (suggestion) => {
  addToHistory({
    type: eventTypes.BOT_UTTERANCE,
    data: {
      text,
      suggestions: [],
      hints: [],
      results: [],
      tags: [],
    },
  });

  sendUtteranceSuggestion(suggestion.sayText || suggestion.displayText);

  sendEvent(eventTypes.PROMPT_SUGGESTION_CLICK, {
    name: suggestion.sayText || suggestion.displayText,
  });
  addUserAction(USER_ACTIONS.PROMPT_SUGGESTION_CLICK);
};

const countBotUtterancesInHistory = pipe(
  filter(propEq("type", eventTypes.BOT_UTTERANCE)),
  length
);

const historyHasUserUtterance = any(propEq("type", eventTypes.USER_UTTERANCE));

export default ({ id, sttController, soundController }) => {
  const [analytics, setAnalytics] = useState();
  const [socketHandlers, setSocketHandlers] = useStateStable();
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [previousFocusElement, setPreviousFocusElement] = useState();

  // `useReducer` is used to get a dispatch function which can be used in external callbacks,
  // i.e. the communication lib.
  const [history, mutateHistory] = useReducer(
    (state, mutator) => mutator(state),
    []
  );

  const [unreadCount, setUnreadCount] = useState({ unRead: 0 });
  const [connectionEvents, addConnectionEvent] = useReducer(
    (state, current) => [...state, current],
    []
  );
  const [isThinking, setIsThinking] = useStateStable(false);
  const [features, setFeatures] = useStateStable(null);
  const [prompt, setPrompt] = useStateStable(null);
  const [transcribedText, setTranscribedText] = useStateStable("");
  const [recordingState, setRecordingState] = useState(
    RECORDING_STATES.NOT_RECORDING
  );
  const [isOpen, setIsOpen] = useState(false);
  const [externalSearchText, setExternalSearchText] = useState("");
  const [userActionHistory, addUserAction] = useReducer(
    (state, userAction) => [...state, userAction],
    []
  );
  const [isHidden, setIsHidden] = useStateStable(false);
  const [theme, setTheme] = useStateStable({});
  const [didGetFeedback, setDidGetFeedback] = useState(false);
  const [historyFromServer, setHistoryFromServer] = useStateStable(null);

  const useStyles = createUseStyles(
    makeStylesFromThemeAndCustomStyles(
      () =>
        mergeDeepRight(defaultStyles, {
          widget: {
            composes: "$defaultStyles",
            "@media only screen and (max-width: 600px), only screen and (max-height: 450px)": {
              borderRadius: "0",
              bottom: "0 !important",
              right: "0 !important",
              maxHeight: "100% !important",
              height: "100% !important",
              width: "stretch !important",
            },
          },
          noOutlineOnFocus: {
            "& button": {
              "&:focus": { outline: "none !important" },
            },
            "& a": {
              "&:focus": { outline: "none !important" },
            },
            "& textarea": { outline: "none !important" },
            "&:focus": { outline: "none !important" },
          },
        }),
      features && features.customWidgetStyle ? features.customWidgetStyle : {}
    )
  );
  const styles = useStyles();

  const prevUserActionHistory = usePrevious(userActionHistory);

  useEffect(() => {
    setRecordingState(RECORDING_STATES.NOT_RECORDING);
    soundController.stop();
  }, [isOpen, setRecordingState, soundController]);

  useEffect(() => {
    if (
      !analytics ||
      !features ||
      (userActionHistory.length && userActionHistory === prevUserActionHistory)
    ) {
      return;
    }
    const newIsOpen =
      features.stayOpen ||
      userActionHistory.reduce(
        reduceActionHistory,
        features.startOpen || features.widgetOpenState === "open"
      );

    setIsOpen(newIsOpen);
    if (newIsOpen) {
      setPrompt(null);
    }

    if (isMobile() && !features.allowScroll) {
      if (newIsOpen) {
        document.body.classList.add("hyro-mobile");
      } else {
        document.body.classList.remove("hyro-mobile");
      }
    }
    analytics.sendEvent(eventTypes.WIDGET_CLICK, {
      name: newIsOpen ? "open" : "close",
    });
  }, [
    userActionHistory,
    prevUserActionHistory,
    analytics,
    setIsOpen,
    features,
    isHidden,
    setDidGetFeedback,
    setPrompt,
  ]);

  useEffect(() => {
    if (!features) {
      return;
    }
    const feedbackInHistory = history.filter(
      (utterance) =>
        utterance.type === eventTypes.USER_UTTERANCE &&
        (utterance.data.utterance === POSITIVE_FEEDBACK_TEXT ||
          utterance.data.utterance === NEGATIVE_FEEDBACK_TEXT)
    ).length;
    if (!userActionHistory.length) {
      setDidGetFeedback(!features.getFeedback || feedbackInHistory);
    }
  }, [features, setDidGetFeedback, history, didGetFeedback, userActionHistory]);

  useDoOnce(() => {
    // TODO (ROM): move WIDGET_LOAD event here after `CONNECTION_EVENT` is merged in nlu-engine.
    analytics.sendEvent(eventTypes.DEVICE_INFO, {
      ...deviceDetect(),
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
    });
    window.dispatchEvent(new CustomEvent("hyro-ready", {}));
  }, analytics && !isHidden);

  useEffect(() => {
    if (features) {
      blockPages(features, setIsHidden, history);
      const interval = window.setInterval(() => {
        blockPages(features, setIsHidden, history);
      }, 2000);
      return () => {
        window.clearInterval(interval);
      };
    }
  }, [features, setIsHidden, history]);

  useEffect(() => {
    if (isConversationEmpty(history) && isOpen && socketHandlers) {
      socketHandlers.sendConversationStart();
    }
  }, [history, isOpen, socketHandlers]);
  useEffect(() => {
    // Assert sending search text after the welcome message to prevent results override.
    if (externalSearchText && isOpen && !isConversationEmpty(history)) {
      sendUtterance(
        mutateHistory,
        socketHandlers,
        analytics
      )(externalSearchText);
      setExternalSearchText("");
    }
  }, [
    analytics,
    mutateHistory,
    history,
    isOpen,
    socketHandlers,
    externalSearchText,
    setExternalSearchText,
  ]);
  useEffect(() => {
    executeStoredCommands(window);
  }, []);

  const shouldPlayNotificationSound =
    analytics &&
    !isConversationEmpty(history) &&
    features &&
    soundController &&
    last(history).type === eventTypes.USER_UTTERANCE &&
    !isOpen &&
    features.notificationSound &&
    !isMobile();

  useEffect(() => {
    if (shouldPlayNotificationSound) {
      soundController.playPreRecordedMessage("notificationSound");
    }
  }, [shouldPlayNotificationSound, soundController]);

  useEffect(() => {
    if (!analytics) return;

    if (isOpen) {
      setUnreadCount({
        unRead: 0,
        read: countBotUtterancesInHistory(history),
      });
      return;
    }

    if (isConversationEmpty(history)) {
      // Add an unread count to draw user's attention to the widget (even though history is empty).
      setTimeout(() => setUnreadCount({ unRead: 1, read: 0 }), 2000);
      return;
    }

    setUnreadCount((prevState) => {
      if (!prevState.read)
        return {
          unRead: 0,
          read: countBotUtterancesInHistory(history),
        };

      return {
        unRead: Math.max(
          0,
          countBotUtterancesInHistory(history) - prevState.read
        ),
        read: prevState.read,
      };
    });
  }, [history, setUnreadCount, isOpen, analytics]);

  useEffect(() => {
    if (!features || !analytics) {
      return;
    }

    const action = last(history);
    if (
      allPass([
        complement(isNil),
        propEq("type", eventTypes.BOT_UTTERANCE),
        complement(equals(last(historyFromServer))),
      ])(action)
    ) {
      doActionSideEffects(action.data, (readResult) =>
        analytics.sendEvent(eventTypes.READ_RESULT, {
          read_result: readResult,
        })
      );
      if (pipe(path(["data", "events"]), complement(isNil))(action)) {
        extractEventsFromAction(
          analytics.sendAnalyticsEvent,
          action.data.events
        );
      }
    }
  }, [historyFromServer, connectionEvents, history, features, analytics]);

  useEffect(() => {
    // For accessibility, the user needs to see an outline around buttons when navigating the page with TAB.
    // Inspired by https://stackoverflow.com/a/51093815/8600822.
    const mousedown = () => setIsMouseDown(true);
    const keydown = () => setIsMouseDown(false);
    document.addEventListener("mousedown", mousedown);
    document.addEventListener("keydown", keydown);

    return () => {
      document.removeEventListener("mousedown", mousedown);
      document.removeEventListener("keydown", keydown);
    };
  }, [setIsMouseDown]);

  useEffect(() => {
    if (features && socketHandlers) {
      setAnalytics(
        analyticsLib({
          sendSocketEvent: socketHandlers.sendEvent,
          sendGAEvents: features.sendGAEvents,
          sendDTMEvents: features.sendDTMEvents,
        })
      );
    }
  }, [socketHandlers, features]);

  useEffect(() => {
    if (socketHandlers && recordingState === RECORDING_STATES.NOT_RECORDING) {
      setTranscribedText("");
      sttController.stopRecording();
      socketHandlers.sendRecordingStreamEnd();
    }
  }, [sttController, recordingState, socketHandlers, setTranscribedText]);

  useDoOnce(() => {
    analytics.sendEvent(eventTypes.PROMPT_LOADED, { prompt });
  }, !!(analytics && prompt));

  useEffect(() => {
    const onKeyUp = (event) => {
      const { key, shiftKey } = event;
      if (
        key === "Tab" &&
        shiftKey &&
        previousFocusElement === document.getElementById(Header.MINIMIZER_ID)
      ) {
        event.preventDefault();
        const lastTabElement = document.getElementById("hyro-record-button");
        setPreviousFocusElement(lastTabElement);
        lastTabElement.focus();
      }

      setPreviousFocusElement(document.activeElement);
      if (key === "Escape") {
        event.preventDefault();
        if (isStateRecording(recordingState)) {
          setRecordingState(RECORDING_STATES.NOT_RECORDING);
        } else if (isOpen) {
          addUserAction(USER_ACTIONS.ESC_CLICK);
        }
      }
    };
    if (isOpen) {
      document.addEventListener("keyup", onKeyUp);
    } else {
      document.removeEventListener("keyup", onKeyUp);
    }
    return () => {
      document.removeEventListener("keyup", onKeyUp);
    };
  }, [
    isOpen,
    addUserAction,
    setPreviousFocusElement,
    previousFocusElement,
    sttController,
    recordingState,
    setRecordingState,
    analytics,
  ]);

  useEffect(() => {
    if (!last(connectionEvents))
      connectSocket({
        id,
        addToHistory: addToHistory(mutateHistory),
        clearHistory: () => mutateHistory(() => []),
        setFeatures,
        setPrompt,
        soundController,
        setIsThinking,
        setSocketHandlers,
        setTheme,
        setDidGetFeedback,
        addConnectionEvent,
        setHistoryFromServer,
      });
  }, [
    isOpen,
    connectionEvents,
    addConnectionEvent,
    soundController,
    sttController,
    id,
    setPrompt,
    mutateHistory,
    setFeatures,
    setIsThinking,
    setSocketHandlers,
    setTheme,
    setHistoryFromServer,
  ]);

  useEffect(() => {
    if (socketHandlers) {
      socketHandlers.on("transcription", (utterance, isFinal, error) => {
        if (error) {
          setTranscribedText("");
          setRecordingState(RECORDING_STATES.NOT_RECORDING);
          return;
        }
        if (isFinal) {
          setRecordingState(RECORDING_STATES.NOT_RECORDING);
          addToHistory(mutateHistory)({
            type: eventTypes.USER_UTTERANCE,
            data: { utterance, type: "voice" },
          });
        } else {
          setTranscribedText(utterance);
        }
      });
    }
  }, [
    id,
    socketHandlers,
    mutateHistory,
    setTranscribedText,
    setFeatures,
    soundController,
    setIsThinking,
    setSocketHandlers,
    setRecordingState,
  ]);

  useEffect(() => {
    if (!analytics) return;
    const handleEvent = ({ detail: { text } }) => {
      addUserAction(USER_ACTIONS.EXTERNAL_SEARCH);
      if (text) {
        analytics.sendEvent(eventTypes.EXTERNAL_SEARCH, {
          utterance: text,
        });
        setExternalSearchText(text);
      }
    };
    window.addEventListener("hyro-external-text-input", handleEvent);
    return () =>
      window.removeEventListener("hyro-external-text-input", handleEvent);
  }, [socketHandlers, analytics, addUserAction]);

  if (!features || !analytics || isHidden || !theme) return null;

  return (
    <ThemeProvider theme={theme}>
      <div
        className={[
          styles.widget,
          isMouseDown ? styles.noOutlineOnFocus : "",
        ].join(" ")}
        id="hyro-widget"
      >
        {isOpen ? (
          <Panels
            sendTextUtterance={sendUtterance(
              mutateHistory,
              socketHandlers,
              analytics
            )}
            sendUtteranceSuggestion={sendUtteranceSuggestion(
              mutateHistory,
              socketHandlers
            )}
            sendEvent={analytics.sendEvent}
            isRecording={isStateRecording(recordingState)}
            stopRecording={() =>
              setRecordingState(RECORDING_STATES.NOT_RECORDING)
            }
            startRecording={juxt([
              () => analytics.sendEvent(eventTypes.START_RECORDING),
              onStartRecordingClick({
                setTranscribedText,
                soundController,
                sttController,
                socketHandlers,
                setRecordingState,
                addToHistory: addToHistory(mutateHistory),
                sendEvent: analytics.sendEvent,
              }),
            ])}
            isThinking={isThinking}
            transcribedText={
              isStateRecording(recordingState) ? transcribedText : ""
            }
            history={history}
            features={features}
            onClose={() => {
              addUserAction(USER_ACTIONS.MINIMIZE_BUTTON);
              if (
                isFeatureEnabled(featureNames.FEEDBACK) &&
                !didGetFeedback &&
                history.length > 1
              ) {
                showFeedback(setPrompt, setDidGetFeedback);
              }
            }}
          />
        ) : null}
        <ClosedWidget
          sendEvent={analytics.sendEvent}
          isOpen={isOpen}
          specialButton={features.specialButton}
          customWidgetPosition={features.customWidgetPosition}
          addUserAction={addUserAction}
          numberOfUnreadMessages={unreadCount.unRead}
          prompt={prompt}
          setPrompt={setPrompt}
          onPromptSuggestionClick={getTextOnSuggestionClick(
            prompt ? prompt.text : "",
            addToHistory(mutateHistory),
            sendUtteranceSuggestion(mutateHistory, socketHandlers),
            analytics.sendEvent,
            addUserAction
          )}
          customWidgetImage={features.customWidgetImage}
          customWidgetStyle={features.customWidgetStyle}
          enableMinimize={features.enableMinimize}
        />
      </div>
      <AnalyticsInjector enabled={features.injectGTM} />
    </ThemeProvider>
  );
};
